import PropTypes from 'prop-types';
import React, { useEffect, useMemo, useState } from 'react';
import CrmDropdown from '../../shared/components/CrmDropdown';
import CrmSearchInput from '../../shared/components/CrmSearchInput';
import DateRangePicker from '../../ReportingDashboard/components/DateRangePicker';
import useIsMobile from '../../shared/hooks/useIsMobile';
import KpiCards from './KpiCards';
import MobileEventList from './MobileEventList';
import MobileFiltersSheet from './MobileFiltersSheet';
import Pagination from './Pagination';
import TabStrip from './TabStrip';
import TicketsTable from './TicketsTable';
import TableSettings from '../../shared/components/TableSettings';
import useTableColumns from '../../ReportingDashboard/hooks/useTableColumns';
import { TICKETS_COLUMNS } from '../constants/ticketsColumnsConfig';
import { formatNumber } from '../../../shared/formattingUtils';
import '../styles/ticketsPage.css';

const axios = require('axios').default;
const qs = require('qs');

const TABS = [
  { key: 'on_sale_now', label: 'On sale now' },
  { key: 'upcoming', label: 'Upcoming' },
  { key: 'past', label: 'Past' },
  { key: 'all', label: 'All' },
];

function TicketsPage({ team, venueOwnerships, canViewArtistGuarantee }) {
  const isMobile = useIsMobile(768);
  const perPage = 20;
  const [activeTab, setActiveTab] = useState('on_sale_now');
  const [venueFilter, setVenueFilter] = useState('');
  const [dateRange, setDateRange] = useState({ start: '', end: '' });
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(1);
  const [userSort, setUserSort] = useState(null);
  const [effectiveSort, setEffectiveSort] = useState('asc');
  const sortDirection = userSort || effectiveSort;
  const [events, setEvents] = useState([]);
  const [kpis, setKpis] = useState({ total_events: 0, total_tickets_sold: 0, total_gross: 0 });
  const [pagination, setPagination] = useState({ total_entries: 0, total_pages: 1, current_page: 1 });
  const [tabCounts, setTabCounts] = useState({ on_sale_now: 0, upcoming: 0, past: 0, all: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [kpisLoading, setKpisLoading] = useState(true);
  const [kpisError, setKpisError] = useState(false);
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);
  const [tableSettingsOpen, setTableSettingsOpen] = useState(false);
  const columns = useMemo(
    () => (canViewArtistGuarantee
      ? TICKETS_COLUMNS
      : TICKETS_COLUMNS.filter((c) => c.id !== 'artist_guarantee')),
    [canViewArtistGuarantee],
  );
  const { visibleColumns, hiddenColumns, handleColumnsChange } = useTableColumns({
    columns,
    localStorageKey: (id) => `ticketsColumns_${id}`,
    teamId: team && team.id,
  });

  useEffect(() => { setPage(1); }, [venueFilter, dateRange.start, dateRange.end, searchTerm, userSort]);

  const changeTab = (tab) => {
    setActiveTab(tab);
    setUserSort(null);
    setPage(1);
  };

  useEffect(() => {
    if (!team || !team.id) return;

    let cancelled = false;
    setLoading(true);
    setError(null);

    const params = { page, per_page: perPage, tab: activeTab };
    if (userSort) params.sort_direction = userSort;
    if (venueFilter) params.venue_name = [venueFilter];
    if (searchTerm) params.search = searchTerm;
    if (dateRange.start) params.start_date = dateRange.start;
    if (dateRange.end) params.end_date = dateRange.end;

    axios.get(`/teams/${team.id}/tickets/data.json`, {
      params,
      paramsSerializer: (p) => qs.stringify(p, { arrayFormat: 'brackets' }),
    })
      .then((res) => {
        if (cancelled) return;
        const data = res.data || {};
        setEvents(data.events || []);
        setPagination(data.pagination || { total_entries: 0, total_pages: 1, current_page: 1 });
        if (data.sort_direction) setEffectiveSort(data.sort_direction);
        if (data.tab_counts) setTabCounts(data.tab_counts);
      })
      .catch(() => {
        if (!cancelled) setError('Failed to load events. Please try again.');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => { cancelled = true; };
  }, [team && team.id, activeTab, venueFilter, dateRange.start, dateRange.end, searchTerm, userSort, page, perPage]);

  useEffect(() => {
    if (!team || !team.id) return undefined;

    let cancelled = false;
    setKpisLoading(true);
    setKpisError(false);

    const params = { tab: activeTab };
    if (venueFilter) params.venue_name = [venueFilter];
    if (searchTerm) params.search = searchTerm;
    if (dateRange.start) params.start_date = dateRange.start;
    if (dateRange.end) params.end_date = dateRange.end;

    axios.get(`/teams/${team.id}/tickets/summary.json`, {
      params,
      paramsSerializer: (p) => qs.stringify(p, { arrayFormat: 'brackets' }),
    })
      .then((res) => {
        if (cancelled) return;
        setKpis((res.data && res.data.kpis) || { total_events: 0, total_tickets_sold: 0, total_gross: 0 });
      })
      .catch(() => {
        if (!cancelled) setKpisError(true);
      })
      .finally(() => {
        if (!cancelled) setKpisLoading(false);
      });

    return () => { cancelled = true; };
  }, [team && team.id, activeTab, venueFilter, dateRange.start, dateRange.end, searchTerm]);

  const exportUrl = useMemo(() => {
    if (!team || !team.id) return '#';
    const params = new URLSearchParams();
    params.set('tab', activeTab);
    if (venueFilter) params.append('venue_name[]', venueFilter);
    if (searchTerm) params.set('search', searchTerm);
    if (dateRange.start) params.set('start_date', dateRange.start);
    if (dateRange.end) params.set('end_date', dateRange.end);
    return `/teams/${team.id}/tickets/export?${params.toString()}`;
  }, [team && team.id, activeTab, venueFilter, dateRange.start, dateRange.end, searchTerm]);

  const venueOptions = useMemo(() => [
    { value: '', label: 'All venues' },
    ...(venueOwnerships || []).map((vo) => ({ value: vo.name, label: vo.name })),
  ], [venueOwnerships]);

  const tabOptions = TABS.map((tab) => {
    const count = tabCounts[tab.key];
    const label = count != null && count > 0
      ? `${tab.label} (${formatNumber(count)})`
      : tab.label;
    return { value: tab.key, label };
  });

  const activeFilterCount = (venueFilter ? 1 : 0)
    + (dateRange.start || dateRange.end ? 1 : 0)
    + (searchTerm ? 1 : 0);

  const timeZone = team && team.time_zone;
  const pageClass = isMobile ? 'tickets-page tickets-page--mobile' : 'tickets-page';
  const filtersBtnClass = `btn btn-setlist-secondary tickets-filters-btn ${activeFilterCount > 0 ? 'active' : ''}`.trim();

  return (
    <div className={pageClass}>
      <div className="tickets-page-header">
        <h1 className="tickets-page-title">Tickets</h1>
        <div className="tickets-page-header-actions">
          {!isMobile && (
            <div className="tickets-table-settings-wrap">
              <button
                type="button"
                className="btn btn-setlist-secondary tickets-page-header-button"
                onClick={() => setTableSettingsOpen((open) => !open)}
              >
                <i className="fal fa-columns" />
                Table Settings
                {hiddenColumns.length > 0 && (
                  <span className="tickets-filters-btn-count">{hiddenColumns.length}</span>
                )}
              </button>
              <TableSettings
                isOpen={tableSettingsOpen}
                onClose={() => setTableSettingsOpen(false)}
                visibleFields={visibleColumns}
                hiddenFields={hiddenColumns}
                onFieldsChange={handleColumnsChange}
              />
            </div>
          )}
          <a href={exportUrl} className="btn btn-setlist-secondary tickets-page-header-button">
            <i className="fal fa-arrow-to-bottom" />
            Export
          </a>
        </div>
      </div>

      {isMobile ? (
        <div className="tickets-mobile-toolbar">
          <div className="tickets-mobile-toolbar-dropdown">
            <CrmDropdown
              value={activeTab}
              options={tabOptions}
              onChange={changeTab}
              placeholder="On sale now"
              enableSearch={false}
              minWidth="100%"
              ariaLabel="Active tab"
            />
          </div>
          <button
            type="button"
            onClick={() => setMobileFiltersOpen(true)}
            className={filtersBtnClass}
          >
            <i className="fal fa-filter" />
            Filters
            {activeFilterCount > 0 && (
              <span className="tickets-filters-btn-count">{activeFilterCount}</span>
            )}
          </button>
        </div>
      ) : (
        <div className="tickets-toolbar">
          <TabStrip
            tabs={TABS}
            activeTab={activeTab}
            setActiveTab={changeTab}
            tabCounts={tabCounts}
          />

          <div className="tickets-filter-group">
            <CrmDropdown
              value={venueFilter}
              options={venueOptions}
              onChange={setVenueFilter}
              placeholder="All venues"
              minWidth={180}
              ariaLabel="Filter by venue"
              buttonStyle={{ height: '46px' }}
            />
            <DateRangePicker
              dateRange={dateRange}
              onChange={setDateRange}
              align="right"
              emptyLabel="All dates"
              buttonStyle={{ height: '46px', borderRadius: 8, fontSize: 14 }}
            />
            <CrmSearchInput
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search events or artists"
              ariaLabel="Search events or artists"
              shortcutLabel={null}
            />
          </div>
        </div>
      )}

      <KpiCards kpis={kpis} isMobile={isMobile} loading={kpisLoading} error={kpisError} />

      {isMobile ? (
        <MobileEventList
          events={events}
          loading={loading}
          error={error}
          activeTab={activeTab}
          timeZone={timeZone}
          team={team}
        />
      ) : (
        <TicketsTable
          events={events}
          loading={loading}
          error={error}
          activeTab={activeTab}
          timeZone={timeZone}
          team={team}
          sortDirection={sortDirection}
          onSort={() => setUserSort(sortDirection === 'asc' ? 'desc' : 'asc')}
          visibleColumns={visibleColumns}
        />
      )}

      <Pagination
        pagination={pagination}
        totalGross={kpis.total_gross}
        loading={loading}
        onPageChange={(p) => setPage(Math.max(1, Math.min(pagination.total_pages, p)))}
        isMobile={isMobile}
      />

      {isMobile && mobileFiltersOpen && (
        <MobileFiltersSheet
          venueFilter={venueFilter}
          setVenueFilter={setVenueFilter}
          venueOptions={venueOptions}
          dateRange={dateRange}
          setDateRange={setDateRange}
          searchTerm={searchTerm}
          setSearchTerm={setSearchTerm}
          onClose={() => setMobileFiltersOpen(false)}
          onClearAll={() => {
            setVenueFilter('');
            setDateRange({ start: '', end: '' });
            setSearchTerm('');
          }}
          activeFilterCount={activeFilterCount}
        />
      )}
    </div>
  );
}

TicketsPage.propTypes = {
  team: PropTypes.object.isRequired,
  venueOwnerships: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.number,
    name: PropTypes.string,
  })),
  canViewArtistGuarantee: PropTypes.bool,
};

TicketsPage.defaultProps = {
  venueOwnerships: [],
  canViewArtistGuarantee: false,
};

export default TicketsPage;
